import { useEffect, useState, useCallback } from 'react'
import { Wallet, Lock, Clock, ArrowUpRight, Send, KeyRound, TrendingUp } from 'lucide-react'
import api from '../api/client'
import Pagination from '../components/Pagination'
import { successModal, errorModal } from '../components/ui'

const money = (v) => 'R$ ' + Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })
const dt = (s) => (s ? new Date(s).toLocaleDateString('pt-BR') : '—')
const STA = {
  previa: 'bg-amber-100 text-amber-700', pendente: 'bg-amber-100 text-amber-700',
  liberada: 'bg-emerald-100 text-emerald-700', paga: 'bg-blue-100 text-blue-700',
  estornado: 'bg-rose-100 text-rose-700',
}

function Resumo({ r }) {
  const cards = [
    { label: 'Saldo Disponível', v: money(r.saldo_disponivel), icon: Wallet, c: 'from-emerald-400 to-teal-400', t: 'text-emerald-700' },
    { label: 'Saldo Bloqueado', v: money(r.saldo_bloqueado), icon: Lock, c: 'from-rose-400 to-pink-400', t: 'text-rose-700' },
    { label: 'Prévia de Ganhos', v: money(r.previa_ganhos), icon: Clock, c: 'from-amber-400 to-orange-400', t: 'text-amber-700' },
    { label: 'Comissões Liberadas', v: money(r.comissoes_liberadas), icon: TrendingUp, c: 'from-indigo-400 to-violet-400', t: 'text-indigo-700' },
    { label: 'Recebido no Ano', v: money(r.total_recebido_ano), icon: ArrowUpRight, c: 'from-blue-400 to-cyan-400', t: 'text-blue-700' },
    { label: 'Total Sacado', v: money(r.total_sacado), icon: ArrowUpRight, c: 'from-gray-400 to-gray-500', t: 'text-gray-700' },
  ]
  return (
    <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
      {cards.map((c) => (
        <div key={c.label} className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
          <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${c.c} flex items-center justify-center shadow mb-2`}><c.icon size={16} className="text-white" /></div>
          <p className="text-xl font-heading font-bold text-gray-900">{c.v}</p>
          <p className={`text-xs font-medium ${c.t} mt-0.5`}>{c.label}</p>
        </div>
      ))}
    </div>
  )
}

export default function MeuFinanceiro() {
  const [r, setR] = useState(null)
  const [ext, setExt] = useState([])
  const [page, setPage] = useState(1)
  const [size, setSize] = useState(20)
  const [meta, setMeta] = useState({ total: 0, pages: 1 })
  const [modal, setModal] = useState(null)   // 'transferir' | 'sacar' | 'senha'
  const [saques, setSaques] = useState([])

  const loadResumo = useCallback(() => api.get('/financeiro/resumo').then((x) => setR(x.data)).catch(() => {}), [])
  const loadExtrato = useCallback((p = 1, sz = size) => {
    api.get(`/financeiro/extrato?page=${p}&limit=${sz}`).then((x) => { setExt(x.data.data || []); setMeta({ total: x.data.total, pages: x.data.pages }); setPage(p) }).catch(() => {})
  }, [size])
  const loadSaques = useCallback(() => api.get('/financeiro/saques?limit=10').then((x) => setSaques(x.data.data || [])).catch(() => {}), [])

  useEffect(() => { loadResumo(); loadExtrato(1); loadSaques() }, [loadResumo, loadExtrato, loadSaques])
  const reload = () => { loadResumo(); loadExtrato(page); loadSaques() }

  if (!r) return <p className="text-gray-500">Carregando carteira...</p>

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-heading font-bold text-gray-900">Minha Carteira</h1>
          <p className="text-sm text-gray-500 mt-0.5">Próximo pagamento previsto: <b>{dt(r.proximo_pagamento)}</b></p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setModal('transferir')} className="flex items-center gap-1.5 rounded-xl bg-white border border-gray-200 px-3 py-2 text-sm font-semibold hover:bg-gray-50"><Send size={15} /> Transferir</button>
          <button onClick={() => setModal('sacar')} className="flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-primary-500 to-accent-500 text-white px-3 py-2 text-sm font-semibold shadow"><ArrowUpRight size={15} /> Sacar</button>
          <button onClick={() => setModal('senha')} title="Senha de transação" className="rounded-xl bg-white border border-gray-200 px-3 py-2 text-sm hover:bg-gray-50"><KeyRound size={15} /></button>
        </div>
      </div>

      <Resumo r={r} />

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-x-auto">
        <div className="px-4 py-3 font-heading font-semibold text-gray-900">Extrato detalhado</div>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-left text-xs uppercase tracking-wider">
            <tr><th className="px-4 py-2">Data</th><th className="px-4 py-2">Evento</th><th className="px-4 py-2">Tipo</th><th className="px-4 py-2">Cliente</th><th className="px-4 py-2">Produto</th><th className="px-4 py-2">Nível</th><th className="px-4 py-2">%</th><th className="px-4 py-2 text-right">Valor</th><th className="px-4 py-2">Status</th></tr>
          </thead>
          <tbody>
            {ext.map((i) => (
              <tr key={i.id} className="border-t border-gray-100">
                <td className="px-4 py-2 text-gray-500">{dt(i.data)}</td>
                <td className="px-4 py-2">{i.evento || '—'}</td>
                <td className="px-4 py-2">{i.tipo}</td>
                <td className="px-4 py-2">{i.cliente || '—'}</td>
                <td className="px-4 py-2 text-gray-500">{i.produto || '—'}</td>
                <td className="px-4 py-2">{i.nivel}</td>
                <td className="px-4 py-2 text-gray-500">{i.percentual || '—'}</td>
                <td className="px-4 py-2 text-right font-medium">{money(i.valor)}</td>
                <td className="px-4 py-2"><span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${STA[i.status] || 'bg-gray-100 text-gray-600'}`}>{i.status}</span></td>
              </tr>
            ))}
            {ext.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-400">Sem lançamentos.</td></tr>}
          </tbody>
        </table>
      </div>
      <Pagination page={page} pages={meta.pages} total={meta.total} pageSize={size} onPage={loadExtrato} onPageSize={(n) => { setSize(n); loadExtrato(1, n) }} />

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
        <h3 className="font-heading font-semibold text-gray-900 mb-2">Histórico de Saques</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-gray-400 text-xs uppercase text-left"><tr><th className="py-1 pr-4">Solicitado</th><th className="py-1 pr-4">Valor</th><th className="py-1 pr-4">NF</th><th className="py-1 pr-4">Pago em</th><th className="py-1 pr-4">Status</th></tr></thead>
            <tbody>
              {saques.map((s) => (
                <tr key={s.id} className="border-t border-gray-50">
                  <td className="py-1.5 pr-4 text-gray-500">{dt(s.solicitado_em)}</td><td className="py-1.5 pr-4">{money(s.valor)}</td>
                  <td className="py-1.5 pr-4 text-gray-500">{s.nf_numero || '—'}</td><td className="py-1.5 pr-4 text-gray-500">{dt(s.pago_em)}</td>
                  <td className="py-1.5 pr-4"><span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${s.status === 'pago' ? 'bg-blue-100 text-blue-700' : s.status === 'rejeitado' ? 'bg-rose-100 text-rose-700' : s.status === 'aprovado' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{s.status}</span></td>
                </tr>
              ))}
              {saques.length === 0 && <tr><td colSpan={5} className="py-3 text-gray-400">Nenhum saque.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {modal === 'transferir' && <ModalTransferir onClose={() => setModal(null)} onDone={reload} disp={r.saldo_disponivel} />}
      {modal === 'sacar' && <ModalSacar onClose={() => setModal(null)} onDone={reload} disp={r.saldo_disponivel} />}
      {modal === 'senha' && <ModalSenha onClose={() => setModal(null)} jaTem={r.tem_senha_transacao} />}
    </div>
  )
}

function Campo({ label, children }) {
  return <div><label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>{children}</div>
}
const inp = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-accent-400'

function ModalShell({ title, children }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl p-6 w-full max-w-md space-y-3">
        <h2 className="text-lg font-heading font-bold text-gray-900">{title}</h2>
        {children}
      </div>
    </div>
  )
}

function ModalTransferir({ onClose, onDone, disp }) {
  const [f, setF] = useState({ destino: '', valor: '', motivo: '', senha_transacao: '' })
  const enviar = async () => {
    try {
      const body = { valor: Number(f.valor || 0), motivo: f.motivo, senha_transacao: f.senha_transacao }
      if (/^\d+$/.test(f.destino.trim())) body.destino_id = Number(f.destino.trim()); else body.destino_codigo = f.destino.trim()
      const r = await api.post('/financeiro/transferencia', body)
      onClose(); onDone(); successModal(`Transferência ${r.data.comprovante} de ${money(r.data.valor)} para ${r.data.destino}.`, { title: 'Transferência concluída' })
    } catch (e) { errorModal(e?.response?.data?.detail || 'Erro na transferência') }
  }
  return (
    <ModalShell title="Transferir saldo">
      <p className="text-xs text-gray-500">Disponível: <b>{money(disp)}</b></p>
      <Campo label="ID ou código do licenciado destino"><input className={inp} value={f.destino} onChange={(e) => setF({ ...f, destino: e.target.value })} placeholder="Ex.: 25 ou TC-XXXX" /></Campo>
      <Campo label="Valor (R$)"><input type="number" className={inp} value={f.valor} onChange={(e) => setF({ ...f, valor: e.target.value })} /></Campo>
      <Campo label="Motivo (opcional)"><input className={inp} value={f.motivo} onChange={(e) => setF({ ...f, motivo: e.target.value })} /></Campo>
      <Campo label="Senha de transação"><input type="password" className={inp} value={f.senha_transacao} onChange={(e) => setF({ ...f, senha_transacao: e.target.value })} /></Campo>
      <div className="flex justify-end gap-2 pt-1"><button onClick={onClose} className="px-4 py-2 text-gray-500">Cancelar</button><button onClick={enviar} className="rounded-xl bg-gradient-to-r from-primary-500 to-accent-500 text-white px-5 py-2 font-semibold">Transferir</button></div>
    </ModalShell>
  )
}

function ModalSacar({ onClose, onDone, disp }) {
  const [f, setF] = useState({ valor: '', chave_pix: '', nf_numero: '', senha_transacao: '' })
  const [file, setFile] = useState(null)
  const podeEnviar = file && f.nf_numero && Number(f.valor) > 0 && f.chave_pix && f.senha_transacao
  const enviar = async () => {
    if (!file) { errorModal('Anexe a Nota Fiscal (obrigatória), emitida em nome da The Conect, no valor do saque.'); return }
    if (!f.nf_numero) { errorModal('Informe o número da Nota Fiscal.'); return }
    try {
      const fd = new FormData()
      fd.append('valor', Number(f.valor || 0)); fd.append('chave_pix', f.chave_pix)
      fd.append('nf_numero', f.nf_numero)
      fd.append('senha_transacao', f.senha_transacao)
      fd.append('arquivo', file)
      await api.post('/financeiro/saque', fd)
      onClose(); onDone(); successModal('Saque solicitado. O valor ficou bloqueado e foi enviado ao financeiro.', { title: 'Saque em análise' })
    } catch (e) { errorModal(e?.response?.data?.detail || 'Erro ao solicitar saque') }
  }
  return (
    <ModalShell title="Solicitar saque">
      <p className="text-xs text-gray-500">Disponível: <b>{money(disp)}</b></p>
      <div className="text-xs bg-blue-50 text-blue-800 rounded-lg px-3 py-2">ℹ️ Valor mínimo para solicitação de saque: <b>R$ 50,00</b>.</div>
      <div className="text-xs bg-amber-50 text-amber-800 rounded-lg px-3 py-2">📄 Obrigatório: anexe a <b>Nota Fiscal emitida em nome da The Conect</b>, no <b>valor do saque</b>.</div>
      <Campo label="Valor (R$)"><input type="number" className={inp} value={f.valor} onChange={(e) => setF({ ...f, valor: e.target.value })} /></Campo>
      <Campo label="Chave Pix"><input className={inp} value={f.chave_pix} onChange={(e) => setF({ ...f, chave_pix: e.target.value })} /></Campo>
      <Campo label="Número da NF *"><input className={inp} value={f.nf_numero} onChange={(e) => setF({ ...f, nf_numero: e.target.value })} /></Campo>
      <Campo label="Nota Fiscal (PDF/XML) *"><input type="file" accept=".pdf,.xml" onChange={(e) => setFile(e.target.files[0])} className="text-sm" />{file && <span className="text-[11px] text-emerald-600 ml-1">{file.name}</span>}</Campo>
      <Campo label="Senha de transação"><input type="password" className={inp} value={f.senha_transacao} onChange={(e) => setF({ ...f, senha_transacao: e.target.value })} /></Campo>
      <div className="flex justify-end gap-2 pt-1"><button onClick={onClose} className="px-4 py-2 text-gray-500">Cancelar</button><button onClick={enviar} disabled={!podeEnviar} className="rounded-xl bg-gradient-to-r from-primary-500 to-accent-500 text-white px-5 py-2 font-semibold disabled:opacity-40">Solicitar saque</button></div>
    </ModalShell>
  )
}

function ModalSenha({ onClose, jaTem }) {
  const [f, setF] = useState({ senha_atual: '', senha: '' })
  const salvar = async () => {
    try { await api.post('/financeiro/senha-transacao', { senha: f.senha, senha_atual: f.senha_atual || undefined }); onClose(); successModal('Senha de transação definida.') }
    catch (e) { errorModal(e?.response?.data?.detail || 'Erro') }
  }
  return (
    <ModalShell title="Senha de transação">
      <p className="text-xs text-gray-500">Usada para confirmar transferências e saques.</p>
      {jaTem && <Campo label="Senha atual"><input type="password" className={inp} value={f.senha_atual} onChange={(e) => setF({ ...f, senha_atual: e.target.value })} /></Campo>}
      <Campo label="Nova senha (mín. 4 dígitos)"><input type="password" className={inp} value={f.senha} onChange={(e) => setF({ ...f, senha: e.target.value })} /></Campo>
      <div className="flex justify-end gap-2 pt-1"><button onClick={onClose} className="px-4 py-2 text-gray-500">Cancelar</button><button onClick={salvar} className="rounded-xl bg-gradient-to-r from-primary-500 to-accent-500 text-white px-5 py-2 font-semibold">Salvar</button></div>
    </ModalShell>
  )
}
